/**
 * useBatchedTruckUpdates Hook
 *
 * Provides a batching mechanism for real-time truck location updates.
 * Instead of triggering a React state update for every WebSocket message,
 * this hook batches updates over a configurable time window and flushes
 * them together, significantly reducing re-render frequency.
 *
 * Key performance benefits:
 * - Reduces React re-renders from N updates to 1 batched update
 * - Configurable batch window (default 100ms = max 10 updates/second)
 * - Maintains data freshness while optimizing for performance
 */

import { useCallback, useEffect, useRef } from 'react';

import { NextBillionAssetLocation } from '~hooks/useNextBillionAssetLocationHistories/models';

/**
 * Default batch window in milliseconds.
 * Updates within this window will be batched together.
 * 100ms provides a good balance between performance and perceived real-time feel.
 */
const DEFAULT_BATCH_WINDOW_MS = 100;

/**
 * Options for configuring the batched updates behavior.
 */
interface UseBatchedTruckUpdatesOptions {
  /**
   * Callback to flush batched updates to state.
   * Receives a map of equipment/device IDs to their latest locations.
   */
  onFlush: (updates: Map<string, NextBillionAssetLocation>) => void;

  /**
   * Batch window in milliseconds.
   * Updates received within this window will be combined into a single state update.
   * @default 100
   */
  batchWindowMs?: number;

  /**
   * Whether batching is enabled.
   * When disabled, updates are passed through immediately.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Return type for the useBatchedTruckUpdates hook.
 */
interface UseBatchedTruckUpdatesResult {
  /**
   * Add a truck location update to the batch.
   * The update will be flushed after the batch window expires.
   */
  addUpdate: (location: NextBillionAssetLocation) => void;

  /**
   * Immediately flush all pending updates.
   * Useful when you need to ensure all updates are applied.
   */
  flushNow: () => void;

  /**
   * Clear all pending updates without flushing.
   * Useful during cleanup.
   */
  clearPending: () => void;
}

/**
 * Hook that batches real-time truck location updates to reduce re-render frequency.
 *
 * Instead of updating state for every WebSocket message (which can be hundreds
 * per second with many trucks), this hook collects updates and flushes them
 * in batches at a configurable interval.
 *
 * @param options - Configuration options for batching behavior
 * @returns Object with methods to add updates, flush, and clear
 *
 * @example
 * ```tsx
 * const { addUpdate } = useBatchedTruckUpdates({
 *   onFlush: (updates) => {
 *     setTruckLocationsById(prev => {
 *       const newState = { ...prev };
 *       updates.forEach((location, id) => {
 *         newState[id] = location;
 *       });
 *       return newState;
 *     });
 *   },
 *   batchWindowMs: 100,
 * });
 *
 * // In WebSocket handler:
 * received: (data) => {
 *   const location = NextBillionAssetLocation.parse(data);
 *   addUpdate(location); // Will be batched
 * }
 * ```
 */
export const useBatchedTruckUpdates = (
  options: UseBatchedTruckUpdatesOptions,
): UseBatchedTruckUpdatesResult => {
  const { onFlush, batchWindowMs = DEFAULT_BATCH_WINDOW_MS, enabled = true } = options;

  // Store pending updates in a Map for O(1) lookups and deduplication
  // Key: equipment ID or device ID
  // Value: latest location for that equipment
  const pendingUpdatesRef = useRef<Map<string, NextBillionAssetLocation>>(new Map());

  // Reference to the flush timeout
  const flushTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store the onFlush callback in a ref to avoid stale closures
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;

  /**
   * Flush all pending updates to state.
   * Called automatically after the batch window expires.
   */
  const flushUpdates = useCallback(() => {
    const updates = pendingUpdatesRef.current;

    if (updates.size === 0) {
      return;
    }

    // Call the flush callback with the batched updates
    onFlushRef.current(new Map(updates));

    // Clear pending updates
    updates.clear();

    // Clear the timeout reference
    flushTimeoutRef.current = null;
  }, []);

  /**
   * Add a truck location update to the pending batch.
   * If batching is disabled, the update is flushed immediately.
   */
  const addUpdate = useCallback(
    (location: NextBillionAssetLocation) => {
      const id = location.equipment?.id ?? location.id;

      if (!enabled) {
        // Batching disabled - flush immediately
        const immediateUpdate = new Map<string, NextBillionAssetLocation>();
        immediateUpdate.set(id, location);
        onFlushRef.current(immediateUpdate);
        return;
      }

      // Add to pending updates (overwrites any previous update for this ID)
      pendingUpdatesRef.current.set(id, location);

      // Schedule a flush if not already scheduled
      if (!flushTimeoutRef.current) {
        flushTimeoutRef.current = setTimeout(flushUpdates, batchWindowMs);
      }
    },
    [enabled, batchWindowMs, flushUpdates],
  );

  /**
   * Immediately flush all pending updates.
   */
  const flushNow = useCallback(() => {
    // Clear any pending timeout
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }

    flushUpdates();
  }, [flushUpdates]);

  /**
   * Clear all pending updates without flushing.
   */
  const clearPending = useCallback(() => {
    // Clear any pending timeout
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }

    pendingUpdatesRef.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
    };
  }, []);

  return {
    addUpdate,
    flushNow,
    clearPending,
  };
};
